import { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useStaffProfile } from '../hooks/useStaffProfile';
import { useTour } from '../tour/TourProvider';
import { ALL_TRACKS, tracksForContext, PageKey, Role } from '../tour/scripts';
import Icon from './Icon';

type Track = (typeof ALL_TRACKS)[number];

interface TopbarProps {
  currentPage: string;
  /** Navigate to a page before starting a tour that runs elsewhere. */
  onNavigate?: (target: string) => void;
  /** Open the settings page (gear in the top-right, before the account). */
  onSettings?: () => void;
}

const kbd: React.CSSProperties = {
  display: 'inline-block', padding: '0 5px',
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 4, fontSize: 10.5, fontFamily: 'var(--font-mono, monospace)',
  color: 'var(--fg-soft, var(--muted))', fontWeight: 700,
};

export function Topbar({ currentPage, onNavigate, onSettings }: TopbarProps) {
  const { user, logout } = useAuth0();
  const me = useStaffProfile();
  const tour = useTour();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  const displayName = me?.displayName || me?.firstName || (user?.email ?? '');
  const roleLabel =
    me?.cimaRole === 'ADMIN' ? 'Administrator'
    : me?.cimaRole === 'MANAGER' ? 'Manager'
    : me?.jobTitle || 'Responder';
  const avatarLetter = (me?.firstName?.[0] || me?.displayName?.[0] || user?.email?.[0] || '?').toUpperCase();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setHelpOpen(false);
    }
    if (helpOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [helpOpen]);

  const role = (tour.role || undefined) as Role | undefined;
  const here = tracksForContext(currentPage as PageKey | undefined, role);
  const elsewhere = ALL_TRACKS.filter((t) => !here.includes(t) && (!t.roles || !role || t.roles.includes(role)));
  const status = tour.state?.tour_progress || {};

  function startHere(t: Track) {
    setHelpOpen(false);
    setTimeout(() => tour.start(t.id), 80);
  }
  function startElsewhere(t: Track) {
    setHelpOpen(false);
    if (t.pages && t.pages[0]) {
      onNavigate?.(t.pages[0]);
      setTimeout(() => tour.start(t.id), 350);
    } else {
      tour.start(t.id);
    }
  }

  const TrackRow = ({ t, muted }: { t: Track; muted?: boolean }) => {
    const st = status[t.id];
    const badge =
      st === 'completed'   ? { label: '✓ Done',      bg: 'rgba(74,222,128,0.20)', fg: 'var(--success)' }
    : st === 'in_progress' ? { label: 'In progress',  bg: 'rgba(99,55,138,0.14)',  fg: 'var(--primary)' }
    : st === 'skipped'     ? { label: 'Skipped',      bg: 'var(--bg)',             fg: 'var(--muted)' }
    : null;
    return (
      <button
        onClick={() => (muted ? startElsewhere(t) : startHere(t))}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '10px 16px',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'inherit', color: 'var(--fg)', opacity: muted ? 0.78 : 1, transition: 'background 0.1s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-dim, rgba(99,55,138,0.06))')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <span style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: muted ? 'var(--bg)' : 'var(--primary)',
          color: muted ? 'var(--fg-soft, var(--muted))' : '#fff',
          border: muted ? '1px solid var(--border)' : 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
        }}>{t.icon ? <Icon name={t.icon} size={15} /> : (t.glyph || '▶')}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{t.label}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, lineHeight: 1.35 }}>{t.summary}</div>
        </span>
        {badge && (
          <span style={{
            padding: '2px 7px', borderRadius: 999, fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
            background: badge.bg, color: badge.fg, flexShrink: 0,
          }}>{badge.label}</span>
        )}
      </button>
    );
  };

  return (
    <header className="cs-topbar">
      <div className="cs-search">
        <Icon name="search" size={17} />
        <input placeholder="Search incidents, people, modules…" aria-label="Search" />
      </div>

      <div className="cs-topbar-spacer" />

      {/* Walkthrough / help */}
      <div ref={helpRef} data-tour="nav.help" style={{ position: 'relative' }}>
        <button className="cs-iconbtn" aria-label="Walkthroughs" aria-expanded={helpOpen}
          onClick={() => setHelpOpen((o) => !o)} title="CIMA walkthrough">
          <Icon name="help" size={19} />
          {!tour.state?.welcome_seen_at && !tour.state?.completed_at && (
            <span className="pip" style={{ background: 'var(--primary)' }} />
          )}
        </button>
        {helpOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 320, maxWidth: 'calc(100vw - 32px)',
            background: 'var(--card)', border: '1px solid var(--border-md)', borderRadius: 12,
            overflow: 'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.28)', zIndex: 100,
          }}>
            <div style={{
              padding: '12px 16px 8px', fontSize: 10, fontWeight: 800, color: 'var(--subtle)',
              textTransform: 'uppercase', letterSpacing: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>CIMA Walkthroughs</span>
              {tour.state?.completed_at && (
                <span style={{
                  padding: '2px 7px', borderRadius: 999, fontSize: 9,
                  background: 'rgba(74,222,128,0.18)', color: '#16A34A', fontWeight: 800, letterSpacing: 0.4,
                }}>Completed</span>
              )}
            </div>

            {here.length > 0 && (
              <>
                <div style={{ padding: '4px 16px 6px', fontSize: 10, fontWeight: 700, color: 'var(--subtle)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  On this page
                </div>
                {here.map((t) => <TrackRow key={t.id} t={t} />)}
              </>
            )}

            {elsewhere.length > 0 && (
              <>
                <div style={{ padding: '8px 16px 6px', marginTop: 4, fontSize: 10, fontWeight: 700, color: 'var(--subtle)', textTransform: 'uppercase', letterSpacing: 0.6, borderTop: '1px solid var(--border)' }}>
                  Other tours
                </div>
                {elsewhere.map((t) => <TrackRow key={t.id} t={t} muted />)}
              </>
            )}

            <div style={{
              padding: '10px 16px 12px', fontSize: 11, lineHeight: 1.5, color: 'var(--subtle)',
              borderTop: '1px solid var(--border)', background: 'var(--bg)',
            }}>
              Press <kbd style={kbd}>Esc</kbd> to exit · <kbd style={kbd}>← →</kbd> to navigate
            </div>
          </div>
        )}
      </div>

      {onSettings && (
        <button className="cs-iconbtn" aria-label="Settings" title="Settings" onClick={onSettings}>
          <Icon name="gear" size={19} />
        </button>
      )}

      <div className="cs-user">
        <div className="meta">
          <div className="nm">{displayName}</div>
          <div className="rl">{roleLabel}</div>
        </div>
        <div className="av" title={user?.email}>{avatarLetter}</div>
        <button className="cs-iconbtn" aria-label="Sign out" title="Sign out"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          <Icon name="logout" size={18} />
        </button>
      </div>
    </header>
  );
}
